import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'
import { MOMENT, ease, progress } from '@/components/landing/film/timeline'
import { siteConfig } from '@/constants/site'

import {
    fixed,
    setAttr,
    setLayer,
    setStyle
} from '@/components/landing/film/overlay-utils'

interface SceneFinaleProps {
    register: RegisterUpdater
    onCta: () => void
}

const NAME = 'ALIF LAMATO'

// Scene 05: impact rings as the wheel locks, then the master signature and the call to action
const SceneFinale: FC<SceneFinaleProps> = ({ register, onCta }): ReactNode => {
    const layerRef = useRef<HTMLDivElement>(null)
    const svgRef = useRef<SVGSVGElement>(null)
    const ringRefs = useRef<Array<SVGCircleElement | null>>([])
    const sealRef = useRef<HTMLDivElement>(null)
    const hairRefs = useRef<Array<HTMLSpanElement | null>>([])
    const charRefs = useRef<Array<HTMLSpanElement | null>>([])
    const roleRef = useRef<HTMLParagraphElement>(null)
    const taglineRef = useRef<HTMLParagraphElement>(null)
    const ctaRef = useRef<HTMLAnchorElement>(null)

    useEffect(() => register((t, _real, frame) => {
        const active = t >= MOMENT.lock - 0.05
        setLayer(layerRef.current, active ? 1 : 0)
        if (!active) return

        setAttr(svgRef.current, 'viewBox', `0 0 ${Math.round(frame.width)} ${Math.round(frame.height)}`)
        ringRefs.current.forEach((ring, i) => {
            const p = progress(t, MOMENT.lock + i * 0.12, MOMENT.lock + 0.95 + i * 0.12)
            const r = frame.wheel.r * (1.02 + ease.outCubic(p) * 1.3)
            setAttr(ring, 'cx', fixed(frame.wheel.x, 1))
            setAttr(ring, 'cy', fixed(frame.wheel.y, 1))
            setAttr(ring, 'r', fixed(Math.max(1, r), 1))
            setAttr(ring, 'stroke-width', fixed(2.2 - p * 1.6, 2))
            setStyle(ring, 'opacity', p > 0 && p < 1 ? fixed(0.9 * Math.pow(1 - p, 1.5), 3) : '0')
        })

        const seal = ease.outBack(progress(t, 20.75, 21.35))
        setStyle(sealRef.current, 'opacity', fixed(progress(t, 20.75, 21.05), 3))
        setStyle(sealRef.current, 'transform', `scale(${fixed(0.4 + seal * 0.6, 3)}) rotate(${fixed((1 - seal) * -90, 2)}deg)`)

        const hair = ease.outExpo(progress(t, 21.0, 21.8))
        hairRefs.current.forEach((line) => setStyle(line, 'transform', `scaleX(${fixed(hair, 3)})`))

        charRefs.current.forEach((char, i) => {
            const p = ease.outExpo(progress(t, 21.0 + i * 0.04, 21.6 + i * 0.04))
            setStyle(char, 'transform', `translateY(${fixed((1 - p) * 110, 2)}%)`)
        })

        const role = ease.outCubic(progress(t, 21.55, 22.25))
        setStyle(roleRef.current, 'opacity', fixed(role, 3))
        setStyle(roleRef.current, 'transform', `translateY(${fixed((1 - role) * 10, 2)}px)`)

        const tagline = ease.outCubic(progress(t, 22.0, 22.8))
        setStyle(taglineRef.current, 'opacity', fixed(tagline * 0.92, 3))
        setStyle(taglineRef.current, 'filter', tagline < 1 ? `blur(${fixed((1 - tagline) * 6, 2)}px)` : 'none')

        const cta = ease.outCubic(progress(t, 22.6, 23.3))
        const ctaElement = ctaRef.current
        setStyle(ctaElement, 'opacity', fixed(cta, 3))
        setStyle(ctaElement, 'transform', `translateY(${fixed((1 - cta) * 16, 2)}px) scale(${fixed(0.96 + cta * 0.04, 3)})`)
        const interactive = cta > 0.6
        setStyle(ctaElement, 'pointer-events', interactive ? 'auto' : 'none')
        if (ctaElement && (ctaElement.tabIndex === 0) !== interactive) {
            ctaElement.tabIndex = interactive ? 0 : -1
            ctaElement.setAttribute('aria-hidden', interactive ? 'false' : 'true')
        }
    }), [register])

    return (
        <div ref={layerRef} className='pointer-events-none absolute inset-0' style={{ opacity: 0, visibility: 'hidden' }}>
            <svg ref={svgRef} className='absolute inset-0 size-full overflow-visible' viewBox='0 0 100 100' aria-hidden='true'>
                {[0, 1].map((i) => (
                    <circle
                        key={i}
                        ref={(element) => {
                            ringRefs.current[i] = element
                        }}
                        r='1'
                        fill='none'
                        stroke='#ffd700'
                        style={{ opacity: 0 }}
                    />
                ))}
            </svg>

            <div className='film-signature absolute inset-x-0 flex flex-col items-center px-[6%] text-center'>
                <div className='flex w-full max-w-[64cqw] items-center gap-[2cqw]'>
                    <span
                        ref={(element) => {
                            hairRefs.current[0] = element
                        }}
                        className='h-px flex-1 origin-right bg-linear-to-l from-gold to-transparent'
                        style={{ transform: 'scaleX(0)' }}
                    />
                    <div ref={sealRef} className='size-[clamp(34px,4.2cqw,60px)] shrink-0' style={{ opacity: 0 }}>
                        <svg viewBox='0 0 60 60' className='size-full' aria-hidden='true'>
                            <circle cx='30' cy='30' r='28.5' fill='rgba(10, 12, 16, 0.7)' stroke='#ffd700' strokeWidth='1.2' />
                            <circle cx='30' cy='30' r='23' fill='none' stroke='#ffd700' strokeOpacity='0.4' strokeWidth='0.8' />
                            <text x='30' y='35.5' textAnchor='middle' fill='#ffd700' fontSize='15' fontWeight='700' style={{ fontFamily: 'var(--font-heading)' }}>
                                AL
                            </text>
                        </svg>
                    </div>
                    <span
                        ref={(element) => {
                            hairRefs.current[1] = element
                        }}
                        className='h-px flex-1 origin-left bg-linear-to-r from-gold to-transparent'
                        style={{ transform: 'scaleX(0)' }}
                    />
                </div>

                <p className='film-signature-name mt-[2.2cqh]' aria-label={NAME}>
                    {Array.from(NAME).map((char, i) => (
                        <span key={i} className='inline-block overflow-hidden align-bottom' aria-hidden='true'>
                            <span
                                ref={(element) => {
                                    charRefs.current[i] = element
                                }}
                                className='inline-block text-gold-metal'
                                style={{ transform: 'translateY(110%)' }}
                            >
                                {char === ' ' ? ' ' : char}
                            </span>
                        </span>
                    ))}
                </p>

                <p ref={roleRef} className='film-eyebrow mt-[1.6cqh] text-titanium-light' style={{ opacity: 0 }}>
                    Wheel Alchemist &amp; Architect
                </p>

                <p ref={taglineRef} className='film-tagline mt-[2.6cqh] font-medium italic text-titanium-light' style={{ opacity: 0 }}>
                    {siteConfig.tagline}
                </p>

                <a
                    ref={ctaRef}
                    href='#commission'
                    onClick={onCta}
                    tabIndex={-1}
                    aria-hidden='true'
                    className='film-cta gold-sheen mt-[3cqh] inline-flex items-center gap-3 rounded-sm border border-gold/80 bg-obsidian/70 px-[clamp(18px,2.4cqw,36px)] py-[clamp(11px,1.5cqh,18px)] font-mono text-[clamp(10px,0.6cqw_+_5px,14px)] font-semibold uppercase tracking-[0.28em] text-gold backdrop-blur-sm transition-colors hover:bg-gold hover:text-obsidian focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                    style={{ opacity: 0, pointerEvents: 'none' }}
                >
                    Experience the fitment
                </a>
            </div>
        </div>
    )
}

export default SceneFinale
